import React from 'react';
import styled, { css } from 'styled-components';

import { Link } from '@rover/kibble/core';
import { FontSize, MQ, Spacing } from '@rover/kibble/styles';
import type { Anchor } from '@rover/types/src/Anchor';

type Props = {
  links: Anchor[];
  columns: number[];
  columnGap?: string;
  lineSpacing?: 'small' | 'large';
  className?: string;
};

const StyledList = styled.ul<Pick<Props, 'columns' | 'columnGap' | 'lineSpacing'>>`
  ${({ columns, columnGap }) => css`
    list-style-type: none;
    padding-left: 0;
    width: 100%;
    column-count: ${columns[columns.length - 1]};
    column-gap: ${columnGap};

    ${MQ.XS_DOWN.toString()} {
      font-size: ${FontSize.L.toString()};
    }

    ${columns.length === 3 &&
    css`
      ${MQ.SM_DOWN.toString()} {
        column-count: ${columns[1]};
      }

      ${MQ.XS_DOWN.toString()} {
        column-count: ${columns[0]};
      }
    `}

    ${columns.length === 2 &&
    css`
      ${MQ.XS_DOWN.toString()} {
        column-count: ${columns[0]};
      }
    `}
  `}

  & > li {
    break-inside: avoid;
    line-height: 150%;
    /* padding and negative margin are needed to combat Safari's 
    unusual layout shifts when items are focused with the keyboard */
    padding: 0.5em;
    margin-bottom: -1em;

    ${({ lineSpacing }) =>
      lineSpacing === 'large'
        ? css`
            ${MQ.SM_UP.toString()} {
              line-height: 250%;
            }
          `
        : ''}
  }

  & > li a {
    display: inline-block;
    width: 100%;
  }
`;

const LinkOrganizer: React.FC<Props> = ({
  links,
  columns,
  columnGap = Spacing.M.toString(),
  lineSpacing = 'small',
  className,
}) => (
  <StyledList
    className={className}
    columns={columns}
    columnGap={columnGap}
    lineSpacing={lineSpacing}
  >
    {links.map(({ href, title }) => (
      <li key={href}>
        <Link href={href}>{title}</Link>
      </li>
    ))}
  </StyledList>
);

export default LinkOrganizer;
